import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Banknote, Smartphone, Check } from "lucide-react";

export default function PayoutDetails() {
  const [existing, setExisting] = useState(null);
  const [method, setMethod] = useState("upi");
  const [vpa, setVpa] = useState("");
  const [bank, setBank] = useState({ account_holder_name: "", account_number: "", ifsc: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/seller/payout").then(({ data }) => {
      if (data && data.method) {
        setExisting(data);
        setMethod(data.method);
        if (data.method === "upi") setVpa(data.vpa || "");
        else setBank({
          account_holder_name: data.account_holder_name || "",
          account_number: data.account_number || "",
          ifsc: data.ifsc || "",
        });
      }
    }).catch((err) => console.warn("payout fetch failed", err));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = method === "upi"
        ? { method: "upi", vpa: vpa.trim() }
        : { method: "bank", account_holder_name: bank.account_holder_name.trim(), account_number: bank.account_number.trim(), ifsc: bank.ifsc.trim().toUpperCase() };
      const { data } = await api.put("/seller/payout", payload);
      setExisting(data);
      toast.success("Payout details saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border-2 border-black brutal-shadow p-5 mt-6" data-testid="payout-details">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <span className="sticker orange">GET PAID</span>
          <h2 className="font-display text-3xl mt-2">PAYOUT DETAILS</h2>
          <p className="font-mono-b text-xs text-black/60 mt-1">Where you want your money when your thrifts sell.</p>
        </div>
        {existing?.method && (
          <span className="inline-flex items-center gap-1 border-2 border-black px-2 py-1 bg-[#D4FF00] font-mono-b text-xs font-bold uppercase" data-testid="payout-status">
            <Check className="w-3 h-3" /> {existing.method === "upi" ? `UPI · ${existing.vpa}` : `BANK · ${existing.account_number?.slice(-4).padStart(existing.account_number?.length || 0, "•")}`}
          </span>
        )}
      </div>

      <form onSubmit={submit} className="mt-4 space-y-4">
        <div className="flex gap-2">
          {[
            { k: "upi", icon: <Smartphone className="w-4 h-4" />, label: "UPI" },
            { k: "bank", icon: <Banknote className="w-4 h-4" />, label: "BANK" },
          ].map((m) => (
            <button
              key={m.k}
              type="button"
              data-testid={`payout-method-${m.k}`}
              onClick={() => setMethod(m.k)}
              className={`flex items-center gap-2 px-4 py-2 border-2 border-black font-mono-b font-bold uppercase text-sm hover-nudge ${method === m.k ? "bg-black text-[#D4FF00]" : "bg-white"}`}
            >
              {m.icon} {m.label}
            </button>
          ))}
        </div>

        {method === "upi" ? (
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">UPI ID / VPA</label>
            <input
              data-testid="payout-vpa"
              required
              value={vpa}
              onChange={(e) => setVpa(e.target.value)}
              placeholder="name@bank"
              className="brutal-input w-full mt-1"
            />
            <p className="text-xs font-mono-b text-black/60 mt-1">Examples: 9999999999@upi, name@ybl, name@paytm</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-mono-b font-bold uppercase">ACCOUNT HOLDER NAME</label>
              <input data-testid="payout-name" required value={bank.account_holder_name} onChange={(e) => setBank({ ...bank, account_holder_name: e.target.value })} className="brutal-input w-full mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">ACCOUNT NUMBER</label>
              <input data-testid="payout-account" required value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} className="brutal-input w-full mt-1" />
            </div>
            <div>
              <label className="text-xs font-mono-b font-bold uppercase">IFSC (11 CHARS)</label>
              <input data-testid="payout-ifsc" required value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} maxLength={11} className="brutal-input w-full mt-1 uppercase" />
            </div>
          </div>
        )}

        <button data-testid="payout-save" disabled={saving} type="submit" className="bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-6 py-2 font-mono-b font-bold uppercase disabled:opacity-50">
          {saving ? "SAVING..." : existing?.method ? "UPDATE" : "SAVE PAYOUT DETAILS"}
        </button>
        <p className="text-xs font-mono-b text-black/60">Stored securely. Only used to send you your earnings when a thrift sells.</p>
      </form>
    </div>
  );
}
